import { ApiError, getSnapshot, getTicket } from '../api/api';
import { makeClientMessage, parseServerMessage } from '../protocol/guards';
import {
  PROTOCOL_VERSION,
  type ClientMessage,
  type DemoRequestMsg,
  type Domain,
  type FeedbackSubmitMsg,
  type HelloMsg,
  type PingMsg,
  type ResyncMsg,
  type ScreenId,
  type ServerMessage,
} from '../protocol/types';
import type { DisplayStore } from './store';

export interface DisplaySocketOptions {
  deviceKey: string;
  screen: ScreenId;
  domains: Domain[];
  store: DisplayStore;
  clientVersion?: string;
  /** Dopo quanti fallimenti consecutivi attivare il polling REST. */
  pollingAfterFailures?: number;
  pollingIntervalMs?: number;
  /** Override dell'URL (sviluppo); altrimenti arriva dal ticket. */
  wsUrlOverride?: string;
  /** Ricarica la pagina (iniettabile per i test). */
  reload?: () => void;
}

const BACKOFF_MIN = 1_000;

/** Accetta URL assoluti (wss://…) o relativi (/ws/display/) risolti sull'host della pagina. */
function resolveWsUrl(u: string): string {
  const url = new URL(u, window.location.href);
  if (url.protocol === 'http:') url.protocol = 'ws:';
  if (url.protocol === 'https:') url.protocol = 'wss:';
  return url.toString();
}
const BACKOFF_MAX = 30_000;

/**
 * Gestisce il ciclo di vita della connessione realtime di un display:
 * ticket → socket → hello → welcome/snapshot → update…
 * con heartbeat, watchdog, backoff esponenziale e fallback REST.
 */
export class DisplaySocket {
  private ws: WebSocket | null = null;
  private stopped = false;
  /** Incrementato a ogni start/stop: invalida connect() asincroni partiti prima (es. StrictMode). */
  private gen = 0;
  /** `gen` dell'attesa del ticket in corso: evita che un evento online avvii un secondo connect(). */
  private connectingGen = -1;
  /** Incrementato a ogni avvio/arresto del polling: scarta le risposte arrivate in ritardo. */
  private pollGen = 0;
  private failures = 0;
  private heartbeatSec = 20;
  private lastSeq = 0;
  private timers: { reconnect?: number; ping?: number; watchdog?: number; poll?: number } = {};
  private pending = new Map<
    string,
    { resolve: () => void; reject: (e: Error) => void; timer: number }
  >();

  constructor(private opts: DisplaySocketOptions) {}

  start(): void {
    this.stopped = false;
    this.gen += 1;
    window.addEventListener('online', this.onWake);
    document.addEventListener('visibilitychange', this.onVisibility);
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.gen += 1;
    window.removeEventListener('online', this.onWake);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.clearTimers();
    this.stopPolling();
    this.ws?.close(1000, 'client stop');
    this.ws = null;
  }

  /* ---------------- API per il tablet ---------------- */

  submitFeedback(payload: FeedbackSubmitMsg['payload']): Promise<void> {
    return this.sendWithAck(makeClientMessage<FeedbackSubmitMsg>('feedback.submit', payload, true));
  }

  requestDemo(payload: DemoRequestMsg['payload']): Promise<void> {
    return this.sendWithAck(makeClientMessage<DemoRequestMsg>('demo.request', payload, true));
  }

  resync(reason: ResyncMsg['payload']['reason'] = 'manual'): void {
    this.send(makeClientMessage<ResyncMsg>('resync', { reason }));
  }

  /* ---------------- Connessione ---------------- */

  private async connect(): Promise<void> {
    if (this.stopped || this.connectingGen === this.gen) return;
    const gen = this.gen;
    this.connectingGen = gen;
    this.opts.store.setConnection({
      // durante il polling il badge resta "aggiornamento lento", non torna a "riconnessione"
      status: this.timers.poll ? 'polling' : this.failures ? 'reconnecting' : 'connecting',
      attempts: this.failures,
    });

    let ticket: string;
    let wsUrl: string;
    try {
      const t = await getTicket(this.opts.deviceKey, this.opts.screen);
      ticket = t.ticket;
      wsUrl = this.opts.wsUrlOverride ?? t.wsUrl;
    } catch (e) {
      if (this.connectingGen === gen) this.connectingGen = -1;
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        this.opts.store.setConnection({
          status: 'unauthorized',
          lastError: 'Device key non valida o revocata',
        });
        this.stopped = true;
        return;
      }
      if (gen === this.gen) this.onFailure(`ticket: ${(e as Error).message}`);
      return;
    }
    if (this.connectingGen === gen) this.connectingGen = -1;
    if (gen !== this.gen || this.stopped) return;

    const ws = new WebSocket(resolveWsUrl(wsUrl));
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws) return; // socket sostituito nel frattempo
      this.send(
        makeClientMessage<HelloMsg>('hello', {
          protocol: PROTOCOL_VERSION,
          ticket,
          screen: this.opts.screen,
          domains: this.opts.domains,
          clientVersion: this.opts.clientVersion ?? import.meta.env.VITE_APP_VERSION ?? 'dev',
          userAgent: navigator.userAgent,
        }),
      );
      this.armWatchdog();
    };

    ws.onmessage = (ev) => {
      if (this.ws !== ws) return;
      const msg = parseServerMessage(ev.data);
      if (!msg) return;
      this.armWatchdog();
      this.handle(msg);
    };

    ws.onclose = (ev) => {
      if (this.ws !== ws) return; // socket vecchio
      this.ws = null;
      this.clearTimers();
      this.rejectPending(new Error('connessione chiusa'));
      if (this.stopped) return;
      if (ev.code === 4403) {
        this.opts.store.setConnection({ status: 'unauthorized', lastError: 'Device key revocata' });
        this.stopped = true;
        return;
      }
      if (ev.code === 4426) {
        this.reload();
        return;
      }
      this.onFailure(`ws close ${ev.code}`);
    };

    ws.onerror = () => {
      /* il dettaglio non è esposto dal browser: gestiamo tutto in onclose */
    };
  }

  private handle(msg: ServerMessage): void {
    // Rilevamento buchi di sequenza: chiediamo uno snapshot completo.
    if (msg.seq !== undefined) {
      if (this.lastSeq && msg.seq !== this.lastSeq + 1 && msg.type !== 'welcome') {
        this.resync('seq_gap');
      }
      this.lastSeq = msg.seq;
    }

    switch (msg.type) {
      case 'welcome':
        this.failures = 0;
        this.heartbeatSec = msg.payload.heartbeatSec;
        this.stopPolling();
        this.startHeartbeat();
        break;
      case 'command':
        this.runCommand(msg.payload.action);
        return;
      case 'ack':
        if (msg.id) this.settle(msg.id);
        return;
      case 'error':
        if (msg.id) this.settle(msg.id, new Error(msg.payload.message));
        if (msg.payload.fatal) this.ws?.close(4401, msg.payload.code);
        break;
    }
    this.opts.store.dispatch(msg);
  }

  private reload(): void {
    (this.opts.reload ?? (() => window.location.reload()))();
  }

  private runCommand(action: string): void {
    if (action === 'reload') this.reload();
    else if (action === 'resync') this.resync('manual');
    // 'identify' e 'set_brightness': da gestire nel componente KioskShell se servono.
  }

  /* ---------------- Heartbeat & watchdog ---------------- */

  private startHeartbeat(): void {
    window.clearInterval(this.timers.ping);
    this.timers.ping = window.setInterval(() => {
      this.send(makeClientMessage<PingMsg>('ping', { clientTime: Date.now() }));
    }, this.heartbeatSec * 1000);
  }

  /** Se non arriva nulla per 2,5 heartbeat, la connessione è considerata morta. */
  private armWatchdog(): void {
    window.clearTimeout(this.timers.watchdog);
    this.timers.watchdog = window.setTimeout(() => {
      this.ws?.close(4000, 'watchdog');
    }, this.heartbeatSec * 2500);
  }

  /* ---------------- Riconnessione & fallback ---------------- */

  private onFailure(reason: string): void {
    this.failures += 1;
    this.opts.store.setConnection({
      status: this.timers.poll ? 'polling' : 'reconnecting',
      attempts: this.failures,
      lastError: reason,
    });

    if (this.failures >= (this.opts.pollingAfterFailures ?? 4)) this.startPolling();

    const exp = Math.min(BACKOFF_MAX, BACKOFF_MIN * 2 ** (this.failures - 1));
    const delay = exp * (0.7 + Math.random() * 0.6); // jitter ±30%
    window.clearTimeout(this.timers.reconnect);
    this.timers.reconnect = window.setTimeout(() => void this.connect(), delay);
  }

  private startPolling(): void {
    if (this.timers.poll) return;
    const token = ++this.pollGen;
    const poll = async () => {
      try {
        const snap = await getSnapshot(this.opts.deviceKey, this.opts.domains);
        if (token !== this.pollGen) return; // il WebSocket nel frattempo è tornato: i suoi dati sono più recenti
        this.opts.store.replaceData(snap.state, snap.serverTime);
        this.opts.store.setConnection({ status: 'polling', lastMessageAt: Date.now() });
      } catch {
        /* resta in reconnecting */
      }
    };
    void poll();
    this.timers.poll = window.setInterval(poll, this.opts.pollingIntervalMs ?? 30_000);
  }

  private stopPolling(): void {
    this.pollGen += 1;
    window.clearInterval(this.timers.poll);
    this.timers.poll = undefined;
  }

  private onWake = () => {
    if (this.stopped || this.ws || this.connectingGen === this.gen) return;
    window.clearTimeout(this.timers.reconnect);
    void this.connect();
  };

  private onVisibility = () => {
    if (document.visibilityState === 'visible') {
      if (this.ws?.readyState === WebSocket.OPEN) this.resync('visibility');
      else this.onWake();
    }
  };

  private clearTimers(): void {
    window.clearTimeout(this.timers.reconnect);
    window.clearInterval(this.timers.ping);
    window.clearTimeout(this.timers.watchdog);
    this.timers.reconnect = this.timers.ping = this.timers.watchdog = undefined;
    this.lastSeq = 0;
  }

  /* ---------------- Invio ---------------- */

  private send(msg: ClientMessage): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  private sendWithAck(msg: ClientMessage & { id?: string }, timeoutMs = 8000): Promise<void> {
    return new Promise((resolve, reject) => {
      const id = msg.id!;
      if (!this.send(msg)) {
        reject(new Error('non connesso'));
        return;
      }
      const timer = window.setTimeout(() => this.settle(id, new Error('timeout ack')), timeoutMs);
      this.pending.set(id, { resolve, reject, timer });
    });
  }

  private settle(id: string, err?: Error): void {
    const p = this.pending.get(id);
    if (!p) return;
    window.clearTimeout(p.timer);
    this.pending.delete(id);
    if (err) p.reject(err);
    else p.resolve();
  }

  private rejectPending(err: Error): void {
    for (const id of [...this.pending.keys()]) this.settle(id, err);
  }
}
